import { StrictMode, type JSX } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";

interface ArtworkFeature {
  title: string;
  artist: string;
  imageUrl: string;
  attribution: string;
}

const featuredArtworks = [
  {
    title: "Water Lilies",
    artist: "Claude Monet",
    imageUrl:
      "https://pub-ac3655131b854f40955ca2b43fa5a112.r2.dev/catalog/artic/water-lilies-aic-16568/preview.jpg",
    attribution: "Art Institute of Chicago, CC0 Open Access"
  },
  {
    title: "Under the Wave off Kanagawa",
    artist: "Katsushika Hokusai",
    imageUrl: "https://images.metmuseum.org/CRDImages/as/web-large/DP130155.jpg",
    attribution: "The Metropolitan Museum of Art, Open Access"
  },
  {
    title: "The Starry Night",
    artist: "Vincent van Gogh",
    imageUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/e/ea/Van_Gogh_-_Starry_Night_-_Google_Art_Project.jpg/1280px-Van_Gogh_-_Starry_Night_-_Google_Art_Project.jpg",
    attribution: "Public domain reproduction via Wikimedia Commons"
  }
] as const satisfies readonly ArtworkFeature[];

const appHighlights = [
  "Browse a curated launch catalog by artist.",
  "Crop, mat, and prepare 16:9 Frame-ready JPEGs on device.",
  "Save prepared artwork locally before sending it to the TV.",
  "Use SmartThings fallback whenever direct upload is not available."
] as const;

function App(): JSX.Element {
  const pathname = normalizePath(window.location.pathname);

  return (
    <main>
      <SiteHeader />
      {pathname === "/privacy" ? (
        <PrivacyPage />
      ) : pathname === "/support" ? (
        <SupportPage />
      ) : (
        <HomePage />
      )}
      <SiteFooter />
    </main>
  );
}

function normalizePath(pathname: string): string {
  const normalized = pathname.replace(/\/+$/, "");
  return normalized.length > 0 ? normalized : "/";
}

function SiteHeader(): JSX.Element {
  return (
    <header className="siteHeader" aria-label="Art Glow site header">
      <a className="brandLockup" href="/" aria-label="Art Glow home">
        <img src="/art-glow-icon.png" alt="" />
        <span>Art Glow</span>
      </a>
      <nav className="siteNav" aria-label="Primary navigation">
        <a href="/#how-it-works">How it works</a>
        <a href="/#catalog">Catalog</a>
        <a href="/privacy">Privacy</a>
        <a href="/support">Support</a>
      </nav>
    </header>
  );
}

function HomePage(): JSX.Element {
  const heroArtwork = featuredArtworks[0];

  return (
    <>
      <section className="hero" id="top">
        <div className="heroCopy">
          <p className="eyebrow">Curated art for Art Mode TVs</p>
          <h1>Art Glow</h1>
          <p className="heroLead">
            A free mobile app for turning museum-quality artwork into clean, local,
            TV-ready displays.
          </p>
          <div className="heroActions" aria-label="Primary actions">
            <a className="primaryAction" href="mailto:hello@artglow.tv?subject=Art%20Glow%20early%20access">
              Join early access
            </a>
            <a className="secondaryAction" href="#how-it-works">
              See the flow
            </a>
          </div>
        </div>

        <div className="heroVisual" aria-label={`${heroArtwork.title} preview in a TV frame`}>
          <div className="wallGlow" />
          <div className="tvFrame">
            <img src={heroArtwork.imageUrl} alt={`${heroArtwork.title} by ${heroArtwork.artist}`} />
          </div>
          <p className="artCredit">
            {heroArtwork.artist}, {heroArtwork.title}. {heroArtwork.attribution}.
          </p>
        </div>
      </section>

      <section className="processSection" id="how-it-works">
        <div className="sectionIntro">
          <p className="eyebrow">Workflow</p>
          <h2>From catalog to living room in a few deliberate steps.</h2>
        </div>
        <ol className="processList">
          <li>
            <span>01</span>
            <strong>Choose by artist</strong>
            <p>Search or browse the curated catalog without accounts or request queues.</p>
          </li>
          <li>
            <span>02</span>
            <strong>Prepare locally</strong>
            <p>Adjust crop, pan, zoom, mat preset, and color before export.</p>
          </li>
          <li>
            <span>03</span>
            <strong>Send to the TV</strong>
            <p>Use direct local upload when compatible, with SmartThings as the fallback path.</p>
          </li>
        </ol>
      </section>

      <section className="catalogSection" id="catalog">
        <div className="galleryRail" aria-label="Featured artwork examples">
          {featuredArtworks.map((artwork) => (
            <figure key={artwork.title}>
              <img src={artwork.imageUrl} alt={`${artwork.title} by ${artwork.artist}`} />
              <figcaption>
                <strong>{artwork.title}</strong>
                <span>{artwork.artist}</span>
              </figcaption>
            </figure>
          ))}
        </div>
        <div className="catalogCopy">
          <p className="eyebrow">Launch scope</p>
          <h2>Built around a licensed, curated catalog.</h2>
          <ul>
            {appHighlights.map((highlight) => (
              <li key={highlight}>{highlight}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="finalCta" aria-label="Early access">
        <p className="eyebrow">Coming soon</p>
        <h2>Bring better art to the screen that is already on your wall.</h2>
        <a className="primaryAction" href="mailto:hello@artglow.tv?subject=Art%20Glow%20early%20access">
          Join early access
        </a>
      </section>
    </>
  );
}

function PrivacyPage(): JSX.Element {
  return (
    <section className="legalPage" aria-labelledby="privacy-title">
      <div className="legalHero">
        <p className="eyebrow">Privacy</p>
        <h1 id="privacy-title">Privacy Policy</h1>
        <p>Last updated May 26, 2026</p>
      </div>

      <div className="legalGrid">
        <article className="legalCard">
          <h2>What Art Glow Does</h2>
          <p>
            Art Glow loads a public catalog of curated artwork, lets you prepare a
            16:9 image locally, and helps you save it to Photos or send it to a
            compatible TV on the same Wi-Fi network.
          </p>
        </article>

        <article className="legalCard">
          <h2>What We Do Not Collect</h2>
          <p>
            The mobile app does not require an account, does not ask for your name
            or email, and does not upload prepared artwork outputs to the Art Glow
            backend.
          </p>
        </article>

        <article className="legalCard">
          <h2>Local Network</h2>
          <p>
            Local network access is used to find Samsung TVs with Art Mode on your
            Wi-Fi. Direct upload runs locally; if it is unavailable, the app keeps
            the prepared image on your device and offers the SmartThings fallback.
          </p>
        </article>

        <article className="legalCard">
          <h2>Photos</h2>
          <p>
            Photos add-only access is used when you choose to save a prepared
            artwork JPEG. Art Glow does not need to browse your photo library for
            the v1 catalog-only workflow.
          </p>
        </article>

        <article className="legalCard legalCardWide">
          <h2>Operational Logs</h2>
          <p>
            The catalog API and hosting providers may process standard operational
            logs such as request time, endpoint, status code, and coarse network
            metadata needed to operate and secure the service. These logs are not
            used for consumer accounts or cross-app tracking.
          </p>
        </article>
      </div>
    </section>
  );
}

function SupportPage(): JSX.Element {
  return (
    <section className="legalPage" aria-labelledby="support-title">
      <div className="legalHero">
        <p className="eyebrow">Support</p>
        <h1 id="support-title">Art Glow Support</h1>
        <p>Contact hello@artglow.tv for help with the app.</p>
      </div>

      <div className="legalGrid">
        <article className="legalCard legalCardWide">
          <h2>What To Include</h2>
          <ul className="legalList">
            <li>Your iPhone or iPad model and iOS version.</li>
            <li>Your Samsung TV model and firmware version, if the issue involves TV upload.</li>
            <li>The artwork title and artist, if the issue involves preparing an image.</li>
            <li>The exact message shown by the app.</li>
          </ul>
          <a className="primaryAction" href="mailto:hello@artglow.tv?subject=Art%20Glow%20Support">
            Email support
          </a>
        </article>

        <article className="legalCard">
          <h2>No Account Required</h2>
          <p>Art Glow is free and does not require a login, subscription, or in-app purchase.</p>
        </article>

        <article className="legalCard">
          <h2>TV Upload Notes</h2>
          <p>
            Direct upload depends on TV model, firmware, LAN behavior, and local
            permission prompts. SmartThings fallback remains available when direct
            upload is not compatible.
          </p>
        </article>
      </div>
    </section>
  );
}

function SiteFooter(): JSX.Element {
  return (
    <footer className="siteFooter">
      <span>Art Glow</span>
      <nav aria-label="Footer navigation">
        <a href="/privacy">Privacy</a>
        <a href="/support">Support</a>
        <a href="mailto:hello@artglow.tv">Contact</a>
      </nav>
    </footer>
  );
}

const root = document.getElementById("root");

if (!root) {
  throw new Error("Root element not found.");
}

createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>
);
